import { useState, useCallback, useEffect } from "react";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import { Icon } from "@/components/icons/Icon";
import InputText from "@/components/input-text/InputText";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { usePhase } from "@/store/usePhase";
import { useDevice } from "@/store/useDevice";
import { ModalMobile } from "@/components/modal/mobile/ModalMobile";
import IconButton from "@/components/buttons/IconButton";

interface CurrentPhaseProps {
  closeInspection: (phaseId: number, esnOut: string) => void;
  cancel: () => void;
  isNaClose?: boolean;
}

const CloseInspectionForm = ({
  closeInspection,
  cancel,
  isNaClose,
}: CurrentPhaseProps) => {
  const translate = useTranslate();
  const currentPhase = usePhase((store) => store.currentPhase!);
  const { allNa } = usePhase();
  const [requiredField, setRequiredField] = useState(false);
  const [esnOut, setEsnOut] = useState("");
  const { isMobile } = useDevice();

  const changeEnsOut = useCallback((newEsn: string) => {
    setEsnOut(newEsn);
  }, []);

  const endInspection = useCallback(() => {
    const esnData = isNaClose || allNa ? "N/A" : esnOut;
    const newEsn = esnData.trim();
    if (newEsn.length === 0) {
      setRequiredField(true);
    } else {
      setRequiredField(false);
      closeInspection(currentPhase, newEsn);
    }
  }, [esnOut, closeInspection, currentPhase, isNaClose, allNa]);

  useEffect(() => {
    return () => {
      setRequiredField(false);
      setEsnOut("");
    };
  }, []);

  if (isMobile) {
    return (
      <ModalMobile>
        <ModalMobile.Header className="border-b">
          <div className="flex justify-between items-center bg-white">
            <h1 className="text-lg">{translate("endOfInspection")}</h1>
            <IconButton
              iconClassName="size-6"
              className="bg-transparent text-ib-red-main"
              icon="close"
              onClick={cancel}
            />
          </div>
        </ModalMobile.Header>
        <ModalMobile.Content className="bg-[#FAFAFA]">
          <div className="flex flex-col gap-4">
            <p className="text-ib-main text-ib-gray-main">
              {translate("confirmEndInspection")}
            </p>
            {!isNaClose && !allNa ? (
              <InputText
                value={esnOut}
                onChange={changeEnsOut}
                size="medium"
                label={translate("esnOut")}
                placeholder={translate("esnOut")}
                required={requiredField}
                requiredAsterisk
              />
            ) : null}
          </div>
        </ModalMobile.Content>
        <ModalMobile.Footer className="bg-white border-t">
          <div className="w-full flex flex-col gap-4 ">
            <PrimaryButton
              colour="red"
              icon="check"
              label={translate("endInspection")}
              onClick={endInspection}
            />
            <SecondaryButton
              colour="red"
              label={translate("cancel")}
              onClick={cancel}
            />
          </div>
        </ModalMobile.Footer>
      </ModalMobile>
    );
  }

  return (
    <div
      data-testid="close-inspection-form"
      className="fixed overflow-auto bg-ib-black bg-opacity-70 w-[100vw] min-w-[100vw] h-[100vh] min-h-[100vh] top-0 right-0 z-[1000]"
    >
      <div className="absolute top-[100px] right-1/2 transform translate-x-1/2 bg-ib-white w-fit p-6 rounded-[2px] shadow-modal-1 w-[568px] max-w-[100vw]">
        <div
          className="w-full flex flex-row mb-4 justify-between items-center"
          role="dialog"
        >
          <div className="font-ib-bold text-[24px] leading-[26px] font-[IberiaHeadlineBold]">
            {translate("endOfInspection")}
          </div>
          <button className="h-6 w-6" onClick={cancel}>
            <Icon name="close" className="size-6 text-ib-red-main" />
          </button>
        </div>
        <p className="text-ib-main mb-4 text-ib-gray-main">
          {translate("confirmEndInspection")}
        </p>
        {!isNaClose && !allNa ? (
          <InputText
            value={esnOut}
            onChange={changeEnsOut}
            size="medium"
            label={translate("esnOut")}
            placeholder={translate("esnOut")}
            required={requiredField}
            requiredAsterisk
          />
        ) : null}
        <div className=" w-full">
          <PrimaryButton
            colour="red"
            icon="checkUnderline"
            label={translate("endInspection")}
            onClick={endInspection}
          />
        </div>
        <div className="mt-4 w-full">
          <SecondaryButton
            colour="red"
            label={translate("cancel")}
            onClick={cancel}
          />
        </div>
      </div>
    </div>
  );
};

export default CloseInspectionForm;
